import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Calendar as CalIcon, Clock, X, Tag, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { getServices } from "../lib/servicesCache";
import { useSettings } from "../context/SettingsContext";

const TIME_SLOTS = ["10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00"];
const STATUSES = ["pending","approved","completed","cancelled"];

/**
 * Generic booking-edit modal used by both User Dashboard and Admin Dashboard.
 *
 * Props:
 *  - booking:        the booking object being edited (required)
 *  - open:           controlled open state
 *  - onClose:        callback when the modal should close
 *  - onSaved:        callback(updatedBooking) after a successful PATCH
 *  - isAdmin:        when true, also renders Status + Price-override fields
 */
export default function EditBookingModal({ booking, open, onClose, onSaved, isAdmin = false }) {
    const { settings } = useSettings();
    const leadMinutes = Math.max(0, parseInt(settings.min_booking_lead_minutes ?? 60, 10) || 0);

    const [date, setDate] = useState("");
    const [time, setTime] = useState("");
    const [notes, setNotes] = useState("");
    const [status, setStatus] = useState("pending");
    const [finalPriceOverride, setFinalPriceOverride] = useState("");
    const [showServiceEdit, setShowServiceEdit] = useState(false);
    const [allServices, setAllServices] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [variantSel, setVariantSel] = useState({});
    const [addonSel, setAddonSel] = useState({});
    const [saving, setSaving] = useState(false);

    // Ticking now so same-day slot filter stays fresh
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 30000);
        return () => clearInterval(id);
    }, []);

    // Reset state every time the modal opens on a different booking
    useEffect(() => {
        if (!open || !booking) return;
        setDate(booking.date || "");
        setTime(booking.time || "");
        setNotes(booking.notes || "");
        setStatus(booking.status || "pending");
        setFinalPriceOverride("");
        setShowServiceEdit(false);
        const svcIds = (booking.services || []).map((s) => s.id).filter(Boolean);
        setSelectedIds(svcIds);
        setVariantSel(booking.variant_selections || {});
        setAddonSel(booking.addon_selections || {});
    }, [booking, open]);

    // Lazy-load services list only when admin opens service edit
    useEffect(() => {
        if (!showServiceEdit || allServices.length) return;
        getServices().then((data) => setAllServices(data || [])).catch(() => {});
    }, [showServiceEdit, allServices.length]);

    const todayLocal = useMemo(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    }, [now]);

    const availableSlots = useMemo(() => {
        if (date !== todayLocal) return TIME_SLOTS;
        const cutoff = now.getHours() * 60 + now.getMinutes() + (isAdmin ? 0 : leadMinutes);
        return TIME_SLOTS.filter((slot) => {
            const [h, m] = slot.split(":").map(Number);
            return h * 60 + m >= cutoff;
        });
    }, [date, todayLocal, leadMinutes, now, isAdmin]);
    const visibleTime = availableSlots.includes(time) ? time : "";
    useEffect(() => { if (time && !availableSlots.includes(time)) setTime(""); }, [date, leadMinutes, time, availableSlots]);

    const leadLabel = useMemo(() => {
        if (leadMinutes <= 0) return null;
        const h = Math.floor(leadMinutes / 60);
        const m = leadMinutes % 60;
        if (h && m) return `${h}h ${m}m`;
        if (h) return `${h} hour${h !== 1 ? "s" : ""}`;
        return `${m} minute${m !== 1 ? "s" : ""}`;
    }, [leadMinutes]);

    const toggleService = (svc) => {
        setSelectedIds((prev) => prev.includes(svc.id) ? prev.filter((x) => x !== svc.id) : [...prev, svc.id]);
    };
    const pickVariant = (svcId, variantId) => setVariantSel((p) => ({ ...p, [svcId]: variantId }));
    const toggleAddon = (svcId, addonId) => setAddonSel((p) => {
        const cur = new Set(p[svcId] || []);
        if (cur.has(addonId)) cur.delete(addonId); else cur.add(addonId);
        return { ...p, [svcId]: Array.from(cur) };
    });

    const save = async () => {
        if (!date || !time) { toast.error("Pick a date and time"); return; }
        if (date === todayLocal && !availableSlots.includes(time)) {
            toast.error(leadLabel ? `Bookings need at least ${leadLabel} lead time — please pick a later slot` : "That time has passed — please pick a later slot");
            return;
        }
        // Build payload — only include changed fields to avoid accidental overwrites
        const payload = {};
        if (date !== booking.date) payload.date = date;
        if (time !== booking.time) payload.time = time;
        if ((notes || "") !== (booking.notes || "")) payload.notes = notes;
        if (showServiceEdit) {
            payload.service_ids = selectedIds;
            payload.variant_selections = variantSel;
            payload.addon_selections = addonSel;
        }
        if (isAdmin) {
            if (status !== booking.status) payload.status = status;
            if (finalPriceOverride !== "" && !isNaN(parseFloat(finalPriceOverride))) {
                payload.final_price = parseFloat(finalPriceOverride);
            }
        }
        if (Object.keys(payload).length === 0) { toast("Nothing to update"); return; }
        setSaving(true);
        try {
            const { data } = await api.patch(`/bookings/${booking.id}`, payload);
            toast.success("Booking updated");
            onSaved?.(data);
            onClose?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail, "Could not update booking"));
        } finally {
            setSaving(false);
        }
    };

    if (!open || !booking) return null;

    // Package promo bookings cannot have services swapped (backend will reject)
    const isPackage = !!booking.promotion_id;

    return (
        <div className="fixed inset-0 z-50 bg-[#3D0C1F]/45 backdrop-blur-sm backdrop-blur-sm flex items-center justify-center p-4" data-testid="edit-booking-modal">
            <div className="bg-[#FBF6F0] border border-[#C29A5B]/40 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#6B1535]/15 sticky top-0 bg-[#FBF6F0] z-10">
                    <div>
                        <div className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262]">Edit booking</div>
                        <div className="font-serif-display text-xl text-[#3D0C1F] mt-0.5">{booking.service_name}</div>
                    </div>
                    <button onClick={onClose} className="text-[#7A5262] hover:text-[#3D0C1F] p-2" data-testid="edit-modal-close" aria-label="Close">
                        <X size={18} />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-5">
                    {/* Date / Time */}
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block">
                            <span className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262] flex items-center gap-2"><CalIcon size={12}/> Date</span>
                            <input type="date" min={isAdmin ? undefined : todayLocal} value={date} onChange={(e) => setDate(e.target.value)}
                                className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 mt-2 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                                data-testid="edit-booking-date" />
                        </label>
                        <label className="block">
                            <span className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262] flex items-center gap-2"><Clock size={12}/> Time</span>
                            <select value={visibleTime} onChange={(e) => setTime(e.target.value)}
                                className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 mt-2 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                                data-testid="edit-booking-time">
                                <option value="">Select time</option>
                                {availableSlots.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </label>
                    </div>
                    {!isAdmin && date === todayLocal && availableSlots.length > 0 && leadLabel && (
                        <p className="text-[0.65rem] text-[#7A5262]">Same-day slots need at least {leadLabel} lead time.</p>
                    )}

                    {/* Notes */}
                    <label className="block">
                        <span className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262]">Notes for the salon</span>
                        <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                            placeholder="Anything we should know? E.g. allergies, preferred stylist…"
                            className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 mt-2 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                            data-testid="edit-booking-notes" />
                    </label>

                    {/* Service swap toggle */}
                    {!isPackage && (
                        <div>
                            {!showServiceEdit ? (
                                <button type="button" onClick={() => setShowServiceEdit(true)}
                                    className="text-[0.7rem] tracking-[0.2em] uppercase text-[#8F622C] hover:text-[#3D0C1F] inline-flex items-center gap-2"
                                    data-testid="edit-booking-show-services">
                                    <Tag size={12}/> Change services / add-ons
                                </button>
                            ) : (
                                <div className="border border-[#C29A5B]/30 p-4 bg-[#6B1535]/[0.03] space-y-3" data-testid="edit-booking-services-panel">
                                    <div className="flex items-center justify-between">
                                        <span className="text-[0.65rem] tracking-[0.22em] uppercase text-[#8F622C]">Pick services</span>
                                        <button type="button" onClick={() => setShowServiceEdit(false)} className="text-[#7A5262] hover:text-[#3D0C1F] text-[0.65rem] tracking-[0.22em] uppercase">Cancel</button>
                                    </div>
                                    {allServices.length === 0 ? (
                                        <div className="text-sm text-[#7A5262] flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading services…</div>
                                    ) : (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
                                            {allServices.map((s) => {
                                                const checked = selectedIds.includes(s.id);
                                                return (
                                                    <div key={s.id} className={`p-3 border ${checked ? "border-[#C29A5B] bg-[#6B1535]/[0.06]" : "border-[#6B1535]/15 bg-[#FDF0EC]"}`}>
                                                        <label className="flex items-start gap-2 cursor-pointer">
                                                            <input type="checkbox" checked={checked} onChange={() => toggleService(s)} className="mt-1 accent-[#6B1535]" data-testid={`edit-service-${s.id}`} />
                                                            <span className="flex-1">
                                                                <span className="text-sm text-[#3D0C1F] block">{s.name}</span>
                                                                <span className="text-[0.65rem] text-[#7A5262]">{s.variants?.length ? `from S$${s.price}` : `S$${s.price}`}</span>
                                                            </span>
                                                        </label>
                                                        {checked && s.variants?.length > 0 && (
                                                            <div className="mt-2 pl-6 space-y-1">
                                                                {s.variants.map((v) => (
                                                                    <label key={v.id} className="flex items-center gap-2 text-xs cursor-pointer">
                                                                        <input type="radio" name={`var-${s.id}`} checked={variantSel[s.id] === v.id} onChange={() => pickVariant(s.id, v.id)} className="accent-[#6B1535]" data-testid={`edit-variant-${s.id}-${v.id}`} />
                                                                        <span className="text-[#3D0C1F]">{v.name}</span>
                                                                        <span className="text-[#7A5262] ml-auto">S${v.price}</span>
                                                                    </label>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {checked && s.addons?.length > 0 && (
                                                            <div className="mt-2 pl-6 space-y-1">
                                                                {s.addons.map((a) => (
                                                                    <label key={a.id} className="flex items-center gap-2 text-xs cursor-pointer">
                                                                        <input type="checkbox" checked={(addonSel[s.id] || []).includes(a.id)} onChange={() => toggleAddon(s.id, a.id)} className="accent-[#6B1535]" data-testid={`edit-addon-${s.id}-${a.id}`} />
                                                                        <span className="text-[#7A5262]">+ {a.name}</span>
                                                                        <span className="text-[#7A5262] ml-auto">+ S${a.price}</span>
                                                                    </label>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {isPackage && (
                        <p className="text-[0.65rem] text-[#7A5262] italic">This is a package promotion — services are fixed. To switch services, cancel and rebook.</p>
                    )}

                    {/* Admin-only controls */}
                    {isAdmin && (
                        <div className="border-t border-[#6B1535]/15 pt-5 space-y-4">
                            <div className="flex items-center gap-2 text-[0.65rem] tracking-[0.22em] uppercase text-[#8F622C]"><ShieldCheck size={12}/> Admin controls</div>
                            <div className="grid grid-cols-2 gap-3">
                                <label className="block">
                                    <span className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262]">Status</span>
                                    <select value={status} onChange={(e) => setStatus(e.target.value)}
                                        className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 mt-2 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                                        data-testid="edit-booking-status">
                                        {STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                                    </select>
                                </label>
                                <label className="block">
                                    <span className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262]">Override final price (S$)</span>
                                    <input type="number" min="0" step="0.01" value={finalPriceOverride}
                                        onChange={(e) => setFinalPriceOverride(e.target.value)}
                                        placeholder={`Current: S$${booking.final_price}`}
                                        className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 mt-2 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                                        data-testid="edit-booking-price" />
                                </label>
                            </div>
                            <p className="text-[0.65rem] text-[#7A5262]">Leave price blank to keep the auto-calculated total. Any value entered here overrides it.</p>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#6B1535]/15 bg-[#FBF6F0] sticky bottom-0">
                    <button onClick={onClose} className="px-4 py-2 text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262] hover:text-[#3D0C1F]" data-testid="edit-booking-cancel">Cancel</button>
                    <button onClick={save} disabled={saving}
                        className="btn-gold disabled:opacity-50 flex items-center gap-2"
                        data-testid="edit-booking-save">
                        {saving && <Loader2 size={14} className="animate-spin"/>} Save changes
                    </button>
                </div>
            </div>
        </div>
    );
}
